/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUtmParams } from "@/hooks/marketing/use-utm-params";
import {
  Calendar,
  CheckCircle,
  Clock,
  Loader2,
  Monitor,
  Sparkles,
  Users,
} from "lucide-react";
import { useState, useCallback } from "react";

// ============================================================================
// TYPES
// ============================================================================

interface DemoRequestFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (leadId: string, calendlyUrl: string) => void;
}

interface FormData {
  email: string;
  name: string;
  company: string;
  phone: string;
  role: string;
  company_size: string;
}

type FormState = "idle" | "loading" | "success" | "error";

const ROLE_OPTIONS = [
  { value: "contractor", label: "Contractor / Tradesperson" },
  { value: "estimator", label: "Estimator" },
  { value: "project_manager", label: "Project Manager" },
  { value: "business_owner", label: "Business Owner" },
  { value: "other", label: "Other" },
] as const;

const COMPANY_SIZE_OPTIONS = [
  { value: "1", label: "Just me" },
  { value: "2-5", label: "2-5 employees" },
  { value: "6-10", label: "6-10 employees" },
  { value: "11-25", label: "11-25 employees" },
  { value: "26-50", label: "26-50 employees" },
  { value: "51+", label: "51+ employees" },
] as const;

const demoHighlights = [
  { icon: Clock, text: "30-minute walkthrough" },
  { icon: Monitor, text: "Live platform demo" },
  { icon: Users, text: "Q&A with our team" },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function DemoRequestForm({
  open,
  onOpenChange,
  onSuccess,
}: DemoRequestFormProps) {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    name: "",
    company: "",
    phone: "",
    role: "",
    company_size: "",
  });
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [calendlyUrl, setCalendlyUrl] = useState<string | null>(null);
  const utmParams = useUtmParams();

  const handleInputChange = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (formState === "error") {
        setFormState("idle");
        setErrorMessage("");
      }
    },
    [formState],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!formData.email || !formData.email.includes("@")) {
        setErrorMessage("Please enter a valid email address");
        setFormState("error");
        return;
      }

      setFormState("loading");
      setErrorMessage("");

      try {
        const response = await fetch("/api/marketing/leads", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: formData.email,
            ...(formData.name && { name: formData.name }),
            ...(formData.company && { company: formData.company }),
            ...(formData.phone && { phone: formData.phone }),
            ...(formData.role && { role: formData.role }),
            ...(formData.company_size && {
              company_size: formData.company_size,
            }),
            source: "demo_request",
            ...utmParams,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || "Something went wrong");
        }

        setFormState("success");
        setCalendlyUrl(data.data?.calendly_url || null);

        if (onSuccess && data.data) {
          onSuccess(data.data.lead_id, data.data.calendly_url);
        }
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Something went wrong",
        );
        setFormState("error");
      }
    },
    [formData, utmParams, onSuccess],
  );

  const handleBookDemo = useCallback(() => {
    if (calendlyUrl) {
      window.open(calendlyUrl, "_blank", "noopener,noreferrer");
      onOpenChange(false);
    }
  }, [calendlyUrl, onOpenChange]);

  const handleClose = useCallback(() => {
    if (formState === "success") {
      setFormData({
        email: "",
        name: "",
        company: "",
        phone: "",
        role: "",
        company_size: "",
      });
      setFormState("idle");
      setCalendlyUrl(null);
    }
    onOpenChange(false);
  }, [formState, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        size="md"
        className="rounded-2xl border border-border bg-card shadow-2xl p-0 overflow-hidden"
      >
        {formState === "success" ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-secondary" />
            </div>
            <h3 className="text-xl font-bold mb-2">You&apos;re all set!</h3>
            <p className="text-muted-foreground mb-6">
              Pick a time that works for you and we&apos;ll walk you through
              everything.
            </p>
            <Button
              onClick={handleBookDemo}
              className="w-full bg-secondary hover:bg-secondary/90 text-white font-medium mb-2"
              size="lg"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Your Demo
            </Button>
            <Button
              variant="ghost"
              onClick={handleClose}
              className="w-full text-muted-foreground"
            >
              I&apos;ll schedule later
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-primary px-6 pt-6 pb-5">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-secondary" />
                  <DialogTitle className="text-primary-foreground text-xl font-bold">
                    Book a Demo
                  </DialogTitle>
                </div>
                <DialogDescription className="text-primary-foreground/70 text-sm">
                  See how Contractor Co-Pilot can transform your estimating
                  workflow in a personalized walkthrough.
                </DialogDescription>
              </DialogHeader>

              {/* Highlights */}
              <div className="flex flex-wrap gap-4 mt-4">
                {demoHighlights.map((item) => (
                  <div
                    key={item.text}
                    className="flex items-center gap-2 text-sm text-primary-foreground/80"
                  >
                    <item.icon className="w-4 h-4 text-secondary flex-shrink-0" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 pb-6 pt-4 space-y-3">
              <Input
                type="email"
                placeholder="Email address *"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                disabled={formState === "loading"}
                className="bg-background"
                aria-label="Email address"
                required
              />

              <Input
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                disabled={formState === "loading"}
                className="bg-background"
                aria-label="Name"
              />

              <div className="grid grid-cols-2 gap-3">
                <Select
                  value={formData.role}
                  onValueChange={(v) => handleInputChange("role", v)}
                  disabled={formState === "loading"}
                >
                  <SelectTrigger className="bg-background" aria-label="Role">
                    <SelectValue placeholder="Your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={formData.company_size}
                  onValueChange={(v) => handleInputChange("company_size", v)}
                  disabled={formState === "loading"}
                >
                  <SelectTrigger
                    className="bg-background"
                    aria-label="Company size"
                  >
                    <SelectValue placeholder="Company size" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Input
                type="text"
                placeholder="Company name"
                value={formData.company}
                onChange={(e) => handleInputChange("company", e.target.value)}
                disabled={formState === "loading"}
                className="bg-background"
                aria-label="Company"
              />

              {formState === "error" && errorMessage && (
                <p className="text-sm text-destructive" role="alert">
                  {errorMessage}
                </p>
              )}

              <Button
                type="submit"
                disabled={formState === "loading" || !formData.email}
                className="w-full bg-secondary hover:bg-secondary/90 text-white font-medium"
              >
                {formState === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Request Demo
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                No spam, ever. We respect your privacy.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TRIGGER BUTTON (for use in CTAs)
// ============================================================================

interface DemoRequestButtonProps {
  children?: React.ReactNode;
  className?: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "pill" | "pill-lg" | "icon";
}

export function DemoRequestButton({
  children = "Book a Demo",
  className,
  variant = "outline",
  size,
}: DemoRequestButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={className}
      >
        <Calendar className="w-4 h-4 mr-2" />
        {children}
      </Button>
      <DemoRequestForm open={open} onOpenChange={setOpen} />
    </>
  );
}
